import { useRef, useState } from "react";
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Ban, CircleCheck, KeyRound, LifeBuoy, LogOut, MonitorCheck, MonitorSmartphone, MoreHorizontal, ShieldCheck, ShieldOff, Trash2, UserCog } from "lucide-react";
import { deleteUser, patchUser, resetTempPassword, revokeSessions } from "../../api/admin.js";
import type { AdminUser } from "../../api/admin.js";
import { ApiClientError } from "../../api/http.js";
import { fetchRecoveryOverview, requestTwoFactorReset } from "../../api/recovery.js";
import { requestTrustOverride } from "../../api/trustOverrides.js";
import { recoverAccount } from "../../app/adminRecovery.js";
import { describeAuthError, generateTempPassword, tempCredentials } from "../../app/authFlow.js";
import { useSession } from "../../app/session.js";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { Dialog } from "../../ui/Dialog.js";
import { Field, TextInput } from "../../ui/forms.js";
import { Alert, Avatar, Badge, Button } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";
import { CredentialView } from "./CreateUserDialog.js";
import type { IssuedCredential } from "./CreateUserDialog.js";
import { UserSessionsDialog } from "./UserSessionsDialog.js";

type Action = "temp" | "recover" | "reset2fa" | "trustDevice" | "promote" | "demote" | "disable" | "enable" | "logout" | "delete";

interface ActionSpec {
  readonly label: string;
  readonly icon: LucideIcon;
  readonly title: string;
  readonly body: string;
  readonly confirm: string;
  readonly danger: boolean;
}

const ACTIONS: Readonly<Record<Action, ActionSpec>> = {
  temp: {
    label: "Neues Einmalpasswort",
    icon: KeyRound,
    title: "Neues Einmalpasswort ausstellen",
    body: "Das bisherige Einmalpasswort wird sofort ungültig. Das neue gilt 72 Stunden.",
    confirm: "Ausstellen",
    danger: false
  },
  recover: {
    label: "Zugang wiederherstellen",
    icon: LifeBuoy,
    title: "Zugang wiederherstellen",
    body: "Für ein vergessenes Master-Passwort: Dein Browser öffnet die Kontoschlüssel mit dem Wiederherstellungsschlüssel der Instanz und stellt ein Einmalpasswort aus. Das ist zunächst nur eine Anfrage: Ein zweiter Administrator mit dem Schlüssel muss zustimmen, und erst nach der Wartezeit wird sie ausgeführt. Bis dahin kann die Person sie über den Link in ihrer E-Mail abbrechen. Danach gilt das bisherige Master-Passwort nicht mehr, alle Sitzungen enden, die Einträge bleiben erhalten. Prüfe vorher, dass wirklich sie es angefragt hat.",
    confirm: "Anfrage stellen",
    danger: true
  },
  reset2fa: {
    label: "Zwei-Faktor zurücksetzen",
    icon: ShieldOff,
    title: "Zwei-Faktor-Anmeldung zurücksetzen",
    body: "Für ein verlorenes Zweitgerät: Authenticator-App, Backup-Codes und Passkeys des Kontos werden entfernt, das Master-Passwort bleibt. Wie bei der Wiederherstellung muss ein zweiter Administrator zustimmen, und die Person kann die Anfrage während der Wartezeit per E-Mail-Link abbrechen.",
    confirm: "Anfrage stellen",
    danger: true
  },
  trustDevice: {
    label: "Nächstes Gerät freigeben",
    icon: MonitorCheck,
    title: "Nächstes Gerät freigeben",
    body: "Für ein Konto, das alle freigegebenen Geräte verloren hat, während die Richtlinie „Neue Geräte freigeben lassen“ gilt. Das ist zunächst nur eine Anfrage: Ein zweiter Administrator muss zustimmen. Danach gilt die nächste Anmeldung des Kontos auf einem neuen Gerät einmalig als freigegeben, höchstens 24 Stunden lang. Die Person bekommt eine E-Mail. Prüfe vorher, dass wirklich sie es angefragt hat.",
    confirm: "Anfrage stellen",
    danger: true
  },
  promote: {
    label: "Zum Administrator machen",
    icon: ShieldCheck,
    title: "Administrator-Rechte vergeben",
    body: "Das Konto kann danach Konten anlegen, sperren und löschen (mit eigener Zwei-Faktor-Anmeldung). Tresor-Inhalte anderer bleiben unlesbar.",
    confirm: "Rechte vergeben",
    danger: false
  },
  demote: {
    label: "Administrator-Rechte entziehen",
    icon: ShieldOff,
    title: "Administrator-Rechte entziehen",
    body: "Das Konto wird zu einem normalen Benutzer. Es muss mindestens ein aktiver Administrator bleiben.",
    confirm: "Rechte entziehen",
    danger: true
  },
  disable: {
    label: "Deaktivieren",
    icon: Ban,
    title: "Konto deaktivieren",
    body: "Alle Sitzungen enden sofort, auch in Desktop-App und Browser-Erweiterung. Anmelden ist nicht mehr möglich, Daten bleiben erhalten.",
    confirm: "Deaktivieren",
    danger: true
  },
  enable: {
    label: "Aktivieren",
    icon: CircleCheck,
    title: "Konto wieder aktivieren",
    body: "Die Person kann sich wieder mit ihrem Master-Passwort anmelden.",
    confirm: "Aktivieren",
    danger: false
  },
  logout: {
    label: "Überall abmelden",
    icon: LogOut,
    title: "Alle Sitzungen beenden",
    body: "Das Konto wird auf allen Geräten abgemeldet, z. B. nach einem verlorenen Laptop. Die Person kann sich danach normal neu anmelden.",
    confirm: "Abmelden",
    danger: true
  },
  delete: {
    label: "Löschen",
    icon: Trash2,
    title: "Konto endgültig löschen",
    body: "Löscht das Konto mit persönlichem Tresor, Schlüsseln, Zwei-Faktor-Daten und Team-Mitgliedschaften. Teams, in denen es das einzige Mitglied ist, werden mitgelöscht. Das lässt sich nicht rückgängig machen. Bei vergessenem Master-Passwort ohne Notfall-Kit ist das der einzige Weg: löschen und neu anlegen.",
    confirm: "Endgültig löschen",
    danger: true
  }
};

function actionsFor(user: AdminUser): Action[] {
  const out: Action[] = [];
  if ((user.status === "pending" || user.status === "expired") && !user.recoveryReset) {
    out.push("temp");
  }
  if (user.recoveryEnrolled && user.status !== "disabled") {
    out.push("recover");
  }
  if (user.twoFactorEnabled && user.status !== "disabled") {
    out.push("reset2fa");
  }
  if (user.status === "active") {
    out.push("trustDevice");
  }
  out.push(user.role === "admin" ? "demote" : "promote");
  if (user.status === "active") {
    out.push("logout");
  }
  out.push(user.status === "disabled" ? "enable" : "disable");
  out.push("delete");
  return out;
}

function StatusBadge(props: { readonly user: AdminUser }): ReactNode {
  const user = props.user;
  if (user.status === "disabled") {
    return <Badge tone="buoy" icon={Ban}>Deaktiviert</Badge>;
  }
  if (user.status === "expired") {
    return <Badge tone="buoy">Einmalpasswort abgelaufen</Badge>;
  }
  if (user.status === "pending") {
    return <Badge tone="neutral">{user.recoveryReset ? "Wartet auf neues Passwort" : "Wartet auf erste Anmeldung"}</Badge>;
  }
  return <Badge tone="mint">Aktiv</Badge>;
}

export function UserRow(props: { readonly user: AdminUser; readonly self: boolean; readonly onChanged: () => void }): ReactNode {
  const { state } = useSession();
  const toast = useToast();
  const user = props.user;
  const menuRef = useRef<HTMLDetailsElement | null>(null);
  const [action, setAction] = useState<Action | undefined>(undefined);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [issued, setIssued] = useState<IssuedCredential | undefined>(undefined);
  const [validFrom, setValidFrom] = useState<number | undefined>(undefined);
  const [devicesOpen, setDevicesOpen] = useState(false);

  function closeMenu(): void {
    if (menuRef.current !== null) {
      menuRef.current.open = false;
    }
  }

  function open(next: Action): void {
    closeMenu();
    setTyped("");
    setError(undefined);
    setAction(next);
  }

  function close(): void {
    if (!busy) {
      setAction(undefined);
      setIssued(undefined);
    }
  }

  async function run(chosen: Action): Promise<void> {
    if (state.status !== "unlocked") {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      if (chosen === "temp") {
        const password = generateTempPassword();
        const proof = await tempCredentials(state.client, user.email, password);
        const updated = await resetTempPassword(user.id, proof.saltB64, proof.authKeyB64);
        setIssued({ email: updated.email, password, expiresAt: updated.tempPasswordExpiresAt });
      } else if (chosen === "recover") {
        const overview = await fetchRecoveryOverview();
        if (overview === null) {
          throw new ApiClientError(409, "Die Admin-Wiederherstellung ist nicht mehr eingerichtet.");
        }
        const recovered = await recoverAccount(state.client, overview, user);
        setValidFrom(recovered.request.executeAfter);
        setIssued({ email: user.email, password: recovered.password, expiresAt: null });
      } else if (chosen === "reset2fa") {
        await requestTwoFactorReset(user.id);
      } else if (chosen === "trustDevice") {
        await requestTrustOverride(user.id);
      } else if (chosen === "promote" || chosen === "demote") {
        await patchUser(user.id, { role: chosen === "promote" ? "admin" : "user" });
      } else if (chosen === "disable" || chosen === "enable") {
        await patchUser(user.id, { disabled: chosen === "disable" });
      } else if (chosen === "logout") {
        await revokeSessions(user.id);
      } else {
        const teams = await deleteUser(user.id);
        toast.notify(teams.length === 0 ? "Konto gelöscht." : `Konto gelöscht, ebenso ${teams.join(", ")}.`, { tone: "success" });
      }
      props.onChanged();
      if (chosen !== "temp" && chosen !== "recover") {
        if (chosen !== "delete") {
          toast.notify(`${user.email}: ${ACTIONS[chosen].confirm} – erledigt.`, { tone: "success" });
        }
        setAction(undefined);
      }
    } catch (err: unknown) {
      setError(err instanceof ApiClientError ? err.message : chosen === "recover" ? describeAuthError(err) : "Die Aktion ist fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  const spec = action === undefined ? undefined : ACTIONS[action];
  const confirmBlocked = action === "delete" && typed.trim().toLowerCase() !== user.email;
  const meta: string[] = [user.role === "admin" ? "Administrator" : "Benutzer"];
  if (user.status === "pending" && user.tempPasswordExpiresAt !== null) {
    meta.push(`Einmalpasswort bis ${formatDateTime(user.tempPasswordExpiresAt)}`);
  } else if (user.lastLoginAt !== null) {
    meta.push(`zuletzt angemeldet ${formatRelativeTime(user.lastLoginAt, Date.now())}`);
  } else {
    meta.push("noch nie angemeldet");
  }

  return (
    <li className="flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Avatar label={user.email} size="sm" />
        <div className="min-w-0">
          <p className="flex items-center gap-2 truncate text-sm font-medium text-foam">
            <span className="truncate">{user.email}</span>
            {props.self ? <Badge tone="solid">Du</Badge> : null}
          </p>
          <p className="truncate text-[0.8125rem] text-mist-2">{meta.join(" · ")}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {user.role === "admin" ? <Badge tone="mint" icon={UserCog}>Admin</Badge> : null}
        {user.recoveryEnrolled ? <Badge tone="neutral" icon={LifeBuoy}>Wiederherstellbar</Badge> : null}
        {user.status === "active" ? (user.twoFactorEnabled ? <Badge tone="neutral" icon={ShieldCheck}>2FA</Badge> : <Badge tone="buoy">ohne 2FA</Badge>) : null}
        <StatusBadge user={user} />
        {props.self ? null : (
          <details ref={menuRef} className="relative">
            <summary
              aria-label={`Aktionen für ${user.email}`}
              className="grid size-8 cursor-pointer list-none place-items-center rounded-md text-mist hover:bg-foam/[0.06] hover:text-foam [&::-webkit-details-marker]:hidden"
            >
              <MoreHorizontal size={16} aria-hidden="true" />
            </summary>
            <ul role="menu" className="absolute right-0 z-20 mt-1 w-60 overflow-hidden rounded-lg border border-line-strong bg-ink-3 py-1 shadow-xl">
              {user.status === "active" ? (
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={(): void => {
                      closeMenu();
                      setDevicesOpen(true);
                    }}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-foam hover:bg-foam/[0.06]"
                  >
                    <MonitorSmartphone size={14} aria-hidden="true" />
                    Angemeldete Geräte
                  </button>
                </li>
              ) : null}
              {actionsFor(user).map((key: Action) => {
                const item = ACTIONS[key];
                const Icon = item.icon;
                return (
                  <li key={key} role="none">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={(): void => open(key)}
                      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-foam/[0.06] ${item.danger ? "text-buoy" : "text-foam"}`}
                    >
                      <Icon size={14} aria-hidden="true" />
                      {item.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </details>
        )}
      </div>

      {devicesOpen ? <UserSessionsDialog userId={user.id} email={user.email} onClose={(): void => setDevicesOpen(false)} /> : null}

      {action !== undefined && spec !== undefined ? (
        <Dialog open onClose={close} dismissible={!busy} title={issued === undefined ? spec.title : action === "recover" ? "Zugang wiederhergestellt" : "Neues Einmalpasswort"} description={issued === undefined ? user.email : undefined} variant="center" size="sm">
          {issued !== undefined ? (
            <>
              {action === "recover" && validFrom !== undefined ? (
                <div className="px-5 pt-5">
                  <Alert tone="info">
                    Die Anfrage ist gestellt. Das Einmalpasswort gilt erst, wenn ein zweiter Administrator zugestimmt hat und die Wartezeit vorbei ist (frühestens {formatDateTime(validFrom)}). Ausführen lässt sie sich im Bereich „Wiederherstellung“.
                  </Alert>
                </div>
              ) : null}
              <CredentialView credential={issued} onDone={close} />
            </>
          ) : (
            <div className="flex flex-col gap-5 p-5">
              <p className="text-sm leading-relaxed text-mist">{spec.body}</p>
              {action === "delete" ? (
                <Field id={`confirm-${user.id}`} label="Zur Bestätigung die E-Mail eingeben">
                  <TextInput id={`confirm-${user.id}`} value={typed} autoComplete="off" autoFocus onChange={(event): void => setTyped(event.target.value)} placeholder={user.email} />
                </Field>
              ) : null}
              {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={close} disabled={busy}>
                  Abbrechen
                </Button>
                <Button variant={spec.danger ? "danger" : "primary"} loading={busy} disabled={confirmBlocked} onClick={(): void => void run(action)}>
                  {spec.confirm}
                </Button>
              </div>
            </div>
          )}
        </Dialog>
      ) : null}
    </li>
  );
}
